import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { GetCompetitionVisitSummary } from "../api/CompetitionSurveyApi";

const CompetitionVisitSummary = () => {
  const { t } = useTranslation("competition");

  const userState = useSelector((state) => state.user);

  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(false);
  const [fileBlob, setFileBlob] = useState(null);

  const token = userState?.token;
  const locationId = userState?.location?.noEmplacement;

  useEffect(() => {
    const loadSummary = async () => {
      if (!token || !locationId) {
        setLoading(false);
        setAvailable(false);
        return;
      }

      try {
        setLoading(true);

        const result = await GetCompetitionVisitSummary(token, locationId);

        if (result.success) {
          setFileBlob(result.blob);
          setAvailable(true);
        } else {
          setFileBlob(null);
          setAvailable(false);
        }
      } catch (error) {
        console.error("Error loading competition visit summary:", error);

        setFileBlob(null);
        setAvailable(false);
      } finally {
        setLoading(false);
      }
    };

    loadSummary();
  }, [token, locationId]);

  const handleDownload = () => {
    if (!fileBlob) {
      return;
    }

    const url = window.URL.createObjectURL(fileBlob);

    const link = document.createElement("a");

    link.href = url;
    link.download = "CompetitiveSummary.xlsx";

    document.body.appendChild(link);

    link.click();

    link.remove();

    window.URL.revokeObjectURL(url);
  };

  // ============================================================
  // Loading
  // ============================================================

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 3,
        }}
      >
        <Stack
          spacing={2}
          alignItems="center"
          sx={{
            color: "text.secondary",
          }}
        >
          <CircularProgress size={38} thickness={4} />

          <Typography variant="body2" color="text.secondary">
            {t("Loading")}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "60vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        px: {
          xs: 2,
          sm: 3,
          md: 4,
        },
        py: {
          xs: 3,
          md: 5,
        },
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 760,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          backgroundColor: "background.paper",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: {
              xs: 3,
              sm: 4,
            },
            py: 3,
            backgroundColor: "grey.50",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                flexShrink: 0,
              }}
            >
              <AssessmentOutlinedIcon fontSize="medium" />
            </Box>

            <Box>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{
                  lineHeight: 1.2,
                }}
              >
                {t("CompetitionVisitSummary")}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.5,
                }}
              >
                {t("CompetitionVisitSummaryDescription")}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <CardContent
          sx={{
            p: {
              xs: 3,
              sm: 4,
            },
          }}
        >
          {available ? (
            <Stack spacing={3}>
              {/* File information */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2.5,
                  borderRadius: 2,
                  backgroundColor: "grey.50",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "success.light",
                    color: "success.dark",
                    flexShrink: 0,
                  }}
                >
                  <DescriptionOutlinedIcon fontSize="large" />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {t("CompetitionSummaryAvailable")}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {t("CompetitionSummaryAvailableDescription")}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              {/* Download */}
              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                justifyContent="space-between"
                alignItems={{
                  xs: "stretch",
                  sm: "center",
                }}
                spacing={2}
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("DownloadSummaryDescription")}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  sx={{
                    minWidth: {
                      xs: "100%",
                      sm: 210,
                    },
                    borderRadius: 2,
                    px: 3,
                    py: 1.25,
                    fontWeight: 700,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "none",
                    },
                  }}
                >
                  {t("DownloadSummary")}
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={3}>
              <Alert
                severity="info"
                icon={<AccessTimeOutlinedIcon />}
                sx={{
                  borderRadius: 2,
                  alignItems: "flex-start",
                  "& .MuiAlert-message": {
                    width: "100%",
                  },
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ mb: 0.5 }}
                >
                  {t("NoSurveySummariesTitle")}
                </Typography>

                <Typography variant="body2">
                  {t("NoSurveySummariesAvailable")}
                </Typography>
              </Alert>

              <Box
                sx={{
                  textAlign: "center",
                  py: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t("NoSurveySummariesHelp")}
                </Typography>
              </Box>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CompetitionVisitSummary;
